import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShip, 
  faUsers, 
  faIndustry, 
  faBalanceScale, 
  faChartLine, 
  faFileAlt, 
  faTruck, 
  faBox, 
  faCheckCircle, 
  faClock,
  faExclamationTriangle,
  faMoneyBillWave,
  faFileInvoice,
  faFileContract,
  faShoppingCart,
  faSitemap,
  faBuilding,
  faGlobe,
  faMountain,
  faTint
} from '@fortawesome/free-solid-svg-icons';
import './ModuleCard.css';

// Import images for preloading
import tradeOpsBg from '../../../assets/images/dashboard/modules/trade-operations-bg.jpg';
import tradeOpsFuelsBg from '../../../assets/images/dashboard/modules/trade-operations-fuels-bg.jpg';
import productModuleBg from '../../../assets/images/dashboard/modules/product-module-bg.jpg';
import crmBg from '../../../assets/images/dashboard/modules/crm-bg.jpg';
import legalBg from '../../../assets/images/dashboard/modules/legal-bg.jpg';
import financialsBg from '../../../assets/images/dashboard/modules/financials-bg.jpg';
import corporateStructureBg from '../../../assets/images/dashboard/modules/corporate-structure-bg.jpg';

const ModuleCard = ({ 
  title, 
  backgroundClass,
  metrics = [], 
  route, 
  isComingSoon = false,
  delay = 0,
  moduleType = 'default'
}) => {
  const history = useHistory();
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [imageLoaded, setImageLoaded] = useState(false);

  // Map background classes to imported images
  const getBackgroundImage = (bgClass) => {
    switch (bgClass) {
      case 'trade-operations-bg':
        return tradeOpsBg;
      case 'trade-operations-fuels-bg':
        return tradeOpsFuelsBg;
      case 'product-module-bg':
        return productModuleBg;
      case 'crm-bg':
        return crmBg;
      case 'legal-bg':
        return legalBg;
      case 'financials-bg':
        return financialsBg;
      case 'corporate-structure-bg':
        return corporateStructureBg;
      default:
        return null;
    }
  };

  // Preload the background image
  useEffect(() => {
    const imageSrc = getBackgroundImage(backgroundClass);
    if (imageSrc) {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        setImageLoaded(true);
      };
      img.onerror = () => {
        // Fallback: still show the card even if image fails
        setImageLoaded(true);
      };
    }
  }, [backgroundClass]);

  // Map module types to FontAwesome icons
  const getModuleIcon = (type) => {
    switch (type) {
      case 'trade-operations':
      case 'trade-operations-solids':
        return faMountain;
      case 'trade-operations-fuels':
        return faTint;
      case 'crm':
        return faUsers;
      case 'product-module':
        return faIndustry;
      case 'legal':
        return faBalanceScale;
      case 'financials':
        return faMoneyBillWave;
      case 'corporate-structure':
        return faSitemap;
      default:
        return faShip;
    }
  };

  // Map metric labels to appropriate icons and color classes
  const getMetricIcon = (label) => {
    const lowerLabel = label.toLowerCase();
    
    if (lowerLabel.includes('deal') || lowerLabel.includes('active')) {
      return faChartLine;
    } else if (lowerLabel.includes('recap') || lowerLabel.includes('document')) {
      return faFileAlt;
    } else if (lowerLabel.includes('deliver')) {
      return faTruck;
    } else if (lowerLabel.includes('product') || lowerLabel.includes('item') || lowerLabel.includes('variant')) {
      return faBox;
    } else if (lowerLabel.includes('contract')) {
      return faFileAlt;
    } else if (lowerLabel.includes('approved') || lowerLabel.includes('kyc')) {
      return faCheckCircle;
    } else if (lowerLabel.includes('pending') || lowerLabel.includes('review')) {
      return faClock;
    } else if (lowerLabel.includes('profile')) {
      return faUsers;
    } else if (lowerLabel.includes('flagged')) {
      return faExclamationTriangle;
    } else if (lowerLabel.includes('outgoing')) {
      return faFileInvoice;
    } else if (lowerLabel.includes('pro-forma') || lowerLabel.includes('proforma') || lowerLabel.includes('captured')) {
      return faFileContract;
    } else if (lowerLabel.includes('incoming')) {
      return faMoneyBillWave;
    } else if (lowerLabel.includes('purchase') || lowerLabel.includes('order')) {
      return faShoppingCart;
    } else if (lowerLabel.includes('entit')) {
      return faBuilding;
    } else if (lowerLabel.includes('consolidat')) {
      return faCheckCircle;
    } else if (lowerLabel.includes('countr')) {
      return faGlobe;
    } else if (lowerLabel.includes('group')) {
      return faSitemap;
    } else {
      return faChartLine;
    }
  };

  // Get color class for metric icon
  const getMetricColorClass = (label) => {
    const lowerLabel = label.toLowerCase();
    
    if (lowerLabel.includes('deal')) {
      return 'icon-deals';
    } else if (lowerLabel.includes('recap') || lowerLabel.includes('document')) {
      return 'icon-recaps';
    } else if (lowerLabel.includes('deliver')) {
      return 'icon-deliveries';
    } else if (lowerLabel.includes('item')) {
      return 'icon-items';
    } else if (lowerLabel.includes('variant')) {
      return 'icon-variants';
    } else if (lowerLabel.includes('contract')) {
      return 'icon-contracts';
    } else if (lowerLabel.includes('approved') || lowerLabel.includes('kyc')) {
      return 'icon-approved';
    } else if (lowerLabel.includes('pending') || lowerLabel.includes('review')) {
      return 'icon-pending';
    } else if (lowerLabel.includes('profile')) {
      return 'icon-profiles';
    } else if (lowerLabel.includes('flagged')) {
      return 'icon-flagged';
    } else if (lowerLabel.includes('outgoing')) {
      return 'icon-outgoing';
    } else if (lowerLabel.includes('pro-forma') || lowerLabel.includes('proforma') || lowerLabel.includes('captured')) {
      return 'icon-captured';
    } else if (lowerLabel.includes('incoming')) {
      return 'icon-incoming';
    } else if (lowerLabel.includes('purchase') || lowerLabel.includes('order')) {
      return 'icon-purchase';
    } else if (lowerLabel.includes('invoice')) {
      return 'icon-invoices';
    } else if (lowerLabel.includes('entit')) {
      return 'icon-profiles';
    } else if (lowerLabel.includes('consolidat')) {
      return 'icon-approved';
    } else if (lowerLabel.includes('countr')) {
      return 'icon-deliveries';
    } else if (lowerLabel.includes('group')) {
      return 'icon-recaps';
    } else {
      return 'icon-deals';
    }
  };

  const handleClick = () => {
    if (!isComingSoon) {
      history.push(route);
    }
  };

  return (
    <div 
      ref={ref}
      className={`module-card ${moduleType} ${inView ? 'visible' : ''} ${isComingSoon ? 'coming-soon' : ''}`}
      style={{ 
        animationDelay: `${delay}ms`,
        cursor: isComingSoon ? 'default' : 'pointer'
      }}
      onClick={handleClick}
    >
      {/* Background Image - will be set via CSS class */}
      <div className={`module-card-background ${backgroundClass} ${imageLoaded ? 'loaded' : ''}`} />

      {/* Gradient Overlay - module-specific */}
      <div className="module-card-overlay" />

      {/* Glassmorphism Container */}
      <div className="module-card-content">
        {/* Header */}
        <div className="module-card-header">
          <div className="module-icon">
            <FontAwesomeIcon icon={getModuleIcon(moduleType)} />
          </div>
          <h3 className="module-title">{title}</h3>
        </div>

        {/* Metrics */}
        {!isComingSoon && metrics.length > 0 ? (
          <div className="module-metrics">
            {metrics.map((metric, index) => (
              <div key={index} className="metric-item">
                <span className={`metric-icon ${getMetricColorClass(metric.label)}`}>
                  <FontAwesomeIcon icon={getMetricIcon(metric.label)} />
                </span>
                <span className="metric-value">{metric.value}</span>
                <span className="metric-label">{metric.label}</span>
              </div>
            ))}
          </div>
        ) : isComingSoon ? (
          <div className="coming-soon-badge">
            <span>Coming Soon</span>
            <p className="coming-soon-description">
              This module will provide advanced features for legal compliance and contract management.
            </p>
          </div>
        ) : null}

        {/* Action Button */}
        <button className="module-action-btn">
          {isComingSoon ? 'Learn More →' : 'View Dashboard →'}
        </button>
      </div>
    </div>
  );
};

export default ModuleCard;

